import DOMPurify from 'dompurify'

// Hook globale: forza rel="noopener noreferrer" su tutti i link con target="_blank".
// Protezione contro reverse-tabnabbing quando l'HTML proviene da email/user-generated
// (un link con target=_blank può altrimenti accedere a window.opener della tab parent).
// Senza DOM (es. test unitari con node --test) DOMPurify espone solo uno stub
// privo di addHook: la registrazione va saltata.
if (DOMPurify.isSupported) {
	DOMPurify.addHook('afterSanitizeAttributes', (node) => {
		if (node.nodeName === 'A' && node.getAttribute('target') === '_blank') {
			node.setAttribute('rel', 'noopener noreferrer')
		}
	})
}

// Configurazione DOMPurify condivisa per il rendering di HTML user-generated
// nei timeline items (commenti, email, interventi, inoltri).
// Più sicuro del sanitizer regex-based artigianale: gestisce iframe/svg/object/embed,
// event handler senza quote (es. onerror=alert(1)), data:/javascript: URI, CSS injection, ecc.
const CONFIG = {
	// Tag permessi: HTML "ricco" di un editor (TinyMCE/Quill) ma niente script/iframe/object.
	// `details`/`summary` per il collapse della firma email.
	ALLOWED_TAGS: [
		'p', 'br', 'span', 'div', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
		'a', 'img',
		'ul', 'ol', 'li',
		'blockquote', 'pre', 'code',
		'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
		'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
		'hr', 'sub', 'sup',
		'details', 'summary',
	],
	// width/height/align/valign/bgcolor/border/cellpadding/cellspacing: attributi
	// presentazionali HTML usati massicciamente dalle email per dimensionare img
	// e impostare il layout via <table>. Senza questi, le immagini perdono la
	// dimensione dichiarata e rendono alla risoluzione nativa (firme/loghi enormi).
	ALLOWED_ATTR: [
		'href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'style',
		'colspan', 'rowspan', 'open',
		'width', 'height', 'align', 'valign', 'bgcolor', 'border', 'cellpadding', 'cellspacing',
	],
	// Whitelist degli schemi per href/src: niente javascript:/data: (eccetto data:image/* via img)
	ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|ftp|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
	ADD_ATTR: ['target'],
	FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math'],
	FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
}

// Parole italiane/inglesi più comuni che marcano l'inizio di una firma email
// (anche senza il separatore RFC "-- "). Usato come fallback quando il body non ha "--".
const SIG_KEYWORDS = [
	'Cordiali saluti', 'Distinti saluti', 'Grazie e saluti', 'Saluti', 'A presto',
	'Buona giornata', 'Buon lavoro',
	'Best regards', 'Kind regards', 'Sincerely', 'Cheers', 'Regards', 'Thanks',
]

function isHtmlContent(s) {
	return /<[a-z][\s\S]*?>/i.test(s)
}

function escapeHtml(s) {
	return s
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
}

// Decodifica TUTTE le entità HTML (named, numeric decimali e hex) in caratteri Unicode.
// Usa il parser nativo del browser via textarea (non esegue HTML, è safe).
// Iterativo (max 5 passate) per gestire i casi di doppio-encoding tipici di alcuni MTA
// che ri-codificano un body già encoded: senza iterazione un "&amp;#160;" decodificato
// una volta diventerebbe "&#160;" e poi escapato di nuovo a "&amp;#160;" letterale.
function decodeAllEntities(s) {
	const isBrowser = typeof document !== 'undefined'
	let prev = null
	let iterations = 0
	while (prev !== s && iterations < 5) {
		prev = s
		if (isBrowser) {
			const txt = document.createElement('textarea')
			txt.innerHTML = s
			s = txt.value
		} else {
			// fallback minimale per ambienti senza DOM (test SSR)
			s = s
				.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
				.replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
				.replace(/&nbsp;/g, ' ')
				.replace(/&amp;/g, '&')
				.replace(/&lt;/g, '<')
				.replace(/&gt;/g, '>')
				.replace(/&quot;/g, '"')
				.replace(/&#39;/g, "'")
		}
		iterations++
	}
	return s
}

// Comprimi run di newline a max 2 consecutive (= 1 paragrafo vuoto visibile).
function collapseNewlines(s) {
	return s.replace(/\n{3,}/g, '\n\n')
}

// Comprimi run di linee vuote nell'HTML renderizzato a max 1.
function collapseEmptyHtmlLines(html) {
	return html
		.replace(/(?:<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>\s*){2,}/gi, '<p><br></p>')
		.replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br><br>')
}

// Cerca l'inizio della firma in plain text. Tre tentativi:
// 1. Separatore RFC "-- " su riga propria (sempre vincente se presente).
// 2. Parola chiave saluto (Saluti, Regards, ...) su riga propria, prendendo l'ULTIMO match
//    valido (resto >= 15 caratteri) per evitare di catturare "Saluti" usato nel testo.
function detectPlainSignatureStart(s) {
	const rfc = s.match(/(?:\n|^)-- ?(?=\n)/)
	if (rfc) return rfc.index + (rfc.index > 0 ? 1 : 0)

	const pattern = SIG_KEYWORDS.map(k => k.replace(/\s+/g, '\\s+')).join('|')
	const re = new RegExp('(?:\\n|^)(?:' + pattern + ')[.,!?]?\\s*(?=\\n)', 'gi')
	let lastMatch = null
	let m
	while ((m = re.exec(s)) !== null) {
		if (s.length - re.lastIndex >= 15) {
			lastMatch = m
		}
	}
	if (!lastMatch) return null
	return lastMatch.index + (lastMatch.index > 0 ? 1 : 0)
}

// Stesso pattern per HTML: cerca `<p>--</p>` o `<p>Saluti</p>` ecc. L'ULTIMO match vince.
function detectHtmlSignatureStart(s) {
	const rfc = s.match(/<p[^>]*>\s*(?:--&nbsp;|-- ?)\s*<\/p>/i)
	if (rfc) return s.indexOf(rfc[0])

	const pattern = SIG_KEYWORDS.map(k => k.replace(/\s+/g, '\\s+')).join('|')
	const re = new RegExp('<p[^>]*>\\s*(?:' + pattern + ')[.,!?]?\\s*<\\/p>', 'gi')
	let lastIdx = null
	let m
	while ((m = re.exec(s)) !== null) {
		if (s.length - re.lastIndex >= 30) {
			lastIdx = m.index
		}
	}
	return lastIdx
}

// Trasforma il body in HTML "leggibile" prima del sanitize:
// - plain text: decode entità + nl2br
// - email signature ("-- " su riga propria, o saluto): wrap in <details class="email-signature-block">
//   per renderla collassabile (toggle nativo browser).
// - linee vuote eccessive: collassate a max 1.
function transformBody(html) {
	if (!html) return ''

	// Strippa <style>/<script> completi (tag + contenuto). DOMPurify rimuove i tag ma
	// di default mantiene il contenuto testuale, facendolo apparire come testo letterale
	// nel render (es. CSS inline dei client email Outlook/Froala).
	html = html
		.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
		.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')

	// Strippa blocchi CSS in plain text. Alcuni mailserver convertono email HTML in
	// plain text mantenendo il contenuto del <style> come testo: 3+ regole CSS-like
	// consecutive (`selector { ... }`) sono rumore visivo, non contenuto utile.
	html = html.replace(/(?:[a-zA-Z.#@][\w.#\-,>\s+~:()@]*\{[^{}]{0,500}\}\s*){3,}/g, '')

	const isPlain = !isHtmlContent(html)

	if (isPlain) {
		const decoded = decodeAllEntities(html)
		const collapsed = collapseNewlines(decoded)
		const sigIdx = detectPlainSignatureStart(collapsed)
		if (sigIdx !== null) {
			const before = escapeHtml(collapsed.substring(0, sigIdx)).replace(/\n/g, '<br>')
			const sig = escapeHtml(collapsed.substring(sigIdx)).replace(/\n/g, '<br>')
			return collapseEmptyHtmlLines(before) +
				`<details class="email-signature-block"><summary>Mostra firma</summary>${sig}</details>`
		}
		return collapseEmptyHtmlLines(escapeHtml(collapsed).replace(/\n/g, '<br>'))
	}

	// HTML
	const sigIdx = detectHtmlSignatureStart(html)
	if (sigIdx !== null) {
		const before = html.substring(0, sigIdx)
		const sig = html.substring(sigIdx)
		return collapseEmptyHtmlLines(before) +
			`<details class="email-signature-block"><summary>Mostra firma</summary>${sig}</details>`
	}
	return collapseEmptyHtmlLines(html)
}

export function sanitizeHtml(html) {
	if (!html) return ''
	return DOMPurify.sanitize(transformBody(html), CONFIG)
}

// Solo DOMPurify, senza transformBody: per l'HTML dell'editor (es. annunci HR)
// che non e' una email, quindi niente nl2br, niente decodifica entita' e
// niente collasso della firma in "Mostra firma".
export function sanitizeRichHtml(html) {
	if (!html) return ''
	return DOMPurify.sanitize(html, CONFIG)
}

// Corpo dei messaggi WhatsApp (mittente esterno) reso con v-html: tutto il
// testo viene escapato e solo dopo gli URL diventano link. L'URL si ferma a
// spazi, virgolette e parentesi angolari, quindi non puo' uscire dall'href.
// Niente nl2br: .message-body usa white-space: pre-wrap.
const URL_RE = /https?:\/\/[^\s<>"']+/g

export function escapeAndLinkify(body) {
	if (body === null || body === undefined || body === '') return ''
	const text = String(body)
	let out = ''
	let last = 0
	let match
	URL_RE.lastIndex = 0
	while ((match = URL_RE.exec(text)) !== null) {
		out += escapeHtml(text.slice(last, match.index))
		const url = escapeHtml(match[0])
		out += '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>'
		last = match.index + match[0].length
	}
	out += escapeHtml(text.slice(last))
	return out
}

export default sanitizeHtml
